import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { saveDocDirectory, saveDocSpaceCategory } from '../../../services/api';
import { Check, FolderPlus, Loader2, Plus, X } from 'lucide-react';
import { DocFiltersContext } from '../DocFiltersContext';

const PROJECTS_URL = '/decoupled-api/group/project/collection?display=decoupled';
const MAX_NAME_LENGTH = 120;

/* ════════════════════════════════════════════════════════════════
   SpaceFormBody — Pure React UI (no ReactiveJSON dependency)
   Props: isOpen, parentId, parentName, onClose, onCreated?
   ════════════════════════════════════════════════════════════════ */

export function SpaceFormBody({ isOpen, parentId = 0, parentName = '', onClose, onCreated }) {
    const navigate = useNavigate();
    const filtersCtx = useContext(DocFiltersContext);

    const isSubDirectory = parentId > 0;
    const isRootSpace = !isSubDirectory;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    const [projectId, setProjectId] = useState('');
    const [projects, setProjects] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [showCreateCat, setShowCreateCat] = useState(false);
    const [catName, setCatName] = useState('');
    const [catParentId, setCatParentId] = useState('');
    const [creatingCat, setCreatingCat] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const spaceCategories = filtersCtx?.spaceCategories ?? [];
    const groupedCategories = useMemo(() => {
        if (!Array.isArray(spaceCategories) || spaceCategories.length === 0) return [];
        const roots = spaceCategories.filter((c) => !c.parentId || c.parentId === 0);
        return roots.map((root) => ({
            root,
            children: spaceCategories.filter((c) => c.parentId === root.id),
        }));
    }, [spaceCategories]);

    useEffect(() => {
        if (isOpen) {
            setName(''); setDescription(''); setProjectId('');
            setSelectedCategoryIds([]); setShowCreateCat(false); setCatName('');
            setCatParentId(''); setError('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !isRootSpace) return;
        fetch(PROJECTS_URL, { credentials: 'include' })
            .then((res) => {
                if (!res.ok) throw new Error(`${res.status}`);
                return res.json();
            })
            .then((data) => {
                const items = (data || []).filter(Boolean).map((item) => {
                    const d = item.data ?? item;
                    return { id: d.id, name: d.name || `Projet #${d.id}` };
                });
                setProjects(items);
            })
            .catch(() => setProjects([]));
    }, [isOpen, isRootSpace]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    const rootCategories = useMemo(
        () => spaceCategories.filter((c) => !c.parentId || c.parentId === 0),
        [spaceCategories],
    );

    const toggleCategory = useCallback((catId) => {
        setSelectedCategoryIds((prev) => {
            const cat = spaceCategories.find((c) => c.id === catId);
            if (!cat) return prev;
            const wasSelected = prev.includes(catId);
            if (wasSelected) {
                let next = prev.filter((id) => id !== catId);
                if (!cat.parentId || cat.parentId === 0) {
                    const childIds = spaceCategories
                        .filter((c) => c.parentId === catId)
                        .map((c) => c.id);
                    next = next.filter((id) => !childIds.includes(id));
                }
                return next;
            }
            let next = [...prev, catId];
            if (cat.parentId && cat.parentId !== 0 && !next.includes(cat.parentId)) {
                next = [...next, cat.parentId];
            }
            return next;
        });
    }, [spaceCategories]);

    const handleCreateCategory = useCallback(async () => {
        const trimmedCat = catName.trim();
        if (!trimmedCat) return;
        setCreatingCat(true);
        try {
            const payload = { name: trimmedCat };
            if (catParentId) payload.parentId = parseInt(catParentId, 10);
            const result = await saveDocSpaceCategory(payload);
            const newId = result?.data?.id || result?.id;
            if (newId) {
                const newCat = {
                    id: newId,
                    name: trimmedCat,
                    parentId: payload.parentId || 0,
                    weight: 0,
                    description: '',
                };
                filtersCtx?.setSpaceCategories?.((prev) => [...prev, newCat]);
                setSelectedCategoryIds((prev) => {
                    let next = [...prev, newId];
                    if (newCat.parentId && newCat.parentId !== 0 && !next.includes(newCat.parentId)) {
                        next = [...next, newCat.parentId];
                    }
                    return next;
                });
            }
            setCatName('');
            setCatParentId('');
            setShowCreateCat(false);
        } catch (err) {
            setError(err.message || 'Erreur lors de la création de la catégorie.');
        } finally {
            setCreatingCat(false);
        }
    }, [catName, catParentId, filtersCtx]);

    const handleSubmit = useCallback(async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError('Le nom est obligatoire.');
            return;
        }
        if (trimmed.length > MAX_NAME_LENGTH) {
            setError(`Le nom ne peut pas dépasser ${MAX_NAME_LENGTH} caractères.`);
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                name: trimmed,
                description: description.trim(),

                parentId,
            };
            if (isRootSpace && projectId) {
                const parsed = parseInt(projectId, 10);
                if (!isNaN(parsed)) payload.projectId = parsed;
            }
            if (isRootSpace && selectedCategoryIds.length > 0) {
                payload.spaceCategoryIds = selectedCategoryIds;
            }
            const result = await saveDocDirectory(payload);
            const newId = result?.data?.id || result?.id;
            onClose();
            if (onCreated) {
                onCreated({ id: newId, name: trimmed, parentId });
            } else if (newId) {
                navigate(`/docs/space/${newId}`);
            }
        } catch (err) {
            setError(err.message || 'Erreur lors de la création.');
        } finally {
            setSaving(false);
        }
    }, [name, description, parentId, projectId, selectedCategoryIds, isRootSpace, onClose, onCreated, navigate]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-stone-200 max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 flex-shrink-0">
                    <div className="flex items-center gap-2.5">
                        <FolderPlus size={18} className="text-blue-600" />
                        <h2 className="text-lg font-semibold text-stone-900">
                            {isSubDirectory ? 'Nouveau répertoire' : 'Nouvel espace'}
                        </h2>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-stone-100 text-stone-400 transition-colors" aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
                    <div>
                        <label className="field-label mb-1.5">
                            Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value.slice(0, MAX_NAME_LENGTH))}
                            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                            placeholder={isSubDirectory ? 'Nom du répertoire…' : "Nom de l'espace…"}
                            autoFocus
                            className="input"
                        />
                    </div>

                    <div>
                        <label className="field-label mb-1.5">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Description optionnelle…"
                            rows={2}
                            className="textarea resize-none"
                        />
                    </div>

                    {isRootSpace && (
                        <div>
                            <label className="field-label mb-1.5">Projet lié</label>
                            <select
                                value={projectId}
                                onChange={(e) => setProjectId(e.target.value)}
                                className="select"
                            >
                                <option value="">Aucun projet</option>
                                {projects.map((p) => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                            <p className="text-xs text-stone-400 mt-1">
                                Associe cet espace à un projet pour accéder à ses tickets.
                            </p>
                        </div>
                    )}

                    {isRootSpace && (
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="field-label">Catégories</label>
                                {filtersCtx?.isAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateCat((v) => !v)}
                                        className="btn btn-ghost btn-sm"
                                    >
                                        <Plus size={12} />
                                        <span>Nouvelle</span>
                                    </button>
                                )}
                            </div>
                            {groupedCategories.length > 0 ? (
                                <div className="p-3 border border-stone-200 rounded-xl bg-white min-h-[42px] space-y-2">
                                    {groupedCategories.map(({ root, children }) => (
                                        <div key={root.id}>
                                            <button
                                                type="button"
                                                onClick={() => toggleCategory(root.id)}
                                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-all ${
                                                    selectedCategoryIds.includes(root.id)
                                                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium'
                                                        : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300 hover:bg-stone-100'
                                                }`}
                                            >
                                                {selectedCategoryIds.includes(root.id) && <Check size={10} />}
                                                {root.name}
                                            </button>
                                            {children.length > 0 && (
                                                <div className="flex flex-wrap gap-1.5 mt-1 ml-4 pl-3 border-l-2 border-stone-100">
                                                    {children.map((child) => (
                                                        <button
                                                            key={child.id}
                                                            type="button"
                                                            onClick={() => toggleCategory(child.id)}
                                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-all ${
                                                                selectedCategoryIds.includes(child.id)
                                                                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium'
                                                                    : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300 hover:bg-stone-100'
                                                            }`}
                                                        >
                                                            {selectedCategoryIds.includes(child.id) && <Check size={10} />}
                                                            {child.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <p className="text-xs text-stone-400 mt-1">
                                        Sélectionner une sous-catégorie sélectionne aussi son parent.
                                    </p>
                                </div>
                            ) : !showCreateCat && (
                                <p className="text-xs text-stone-400 italic">
                                    {filtersCtx?.isAdmin
                                        ? 'Aucune catégorie disponible. Créez-en une avec le bouton ci-dessus.'
                                        : 'Aucune catégorie disponible.'}
                                </p>
                            )}
                            {showCreateCat && (
                                <div className="mt-2 p-3 border border-blue-200 bg-blue-50/30 rounded-xl space-y-2">
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            type="text"
                                            value={catName}
                                            onChange={(e) => setCatName(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                                            placeholder="Nom de la catégorie…"
                                            className="input flex-1"
                                        />
                                        <select
                                            value={catParentId}
                                            onChange={(e) => setCatParentId(e.target.value)}
                                            className="select sm:w-auto w-full"
                                        >
                                            <option value="">Racine</option>
                                            {rootCategories.map((c) => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <button
                                            type="button"
                                            onClick={() => { setShowCreateCat(false); setCatName(''); setCatParentId(''); }}
                                            className="btn btn-secondary btn-sm"
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCreateCategory}
                                            disabled={creatingCat || !catName.trim()}
                                            className="btn btn-primary btn-sm"
                                        >
                                            {creatingCat && <Loader2 size={12} className="animate-spin" />}
                                            Créer
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {isRootSpace && (
                        <p className="text-xs text-stone-500 bg-stone-50 px-3 py-2.5 rounded-lg flex items-center gap-2">
                            <span className="text-stone-400">🔒</span>
                            Vous serez automatiquement ajouté comme administrateur de cet espace. Vous pourrez configurer les accès après création.
                        </p>
                    )}
                    {isSubDirectory && (
                        <p className="text-xs text-stone-500 bg-stone-50 px-3 py-2.5 rounded-lg flex items-center gap-2">
                            <span className="text-stone-400">🔗</span>
                            {parentName
                                ? `Hérite des accès de « ${parentName} ».`
                                : 'Hérite des accès du répertoire parent.'}
                        </p>
                    )}

                    {error && (
                        <p className="alert alert-danger">{error}</p>
                    )}
                </div>

                <div className="flex justify-end gap-3 px-6 py-4 border-t border-stone-100 flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-secondary"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving || !name.trim()}
                        className="btn btn-primary"
                    >
                        {saving && <Loader2 size={14} className="animate-spin" />}
                        {isSubDirectory ? 'Créer le répertoire' : "Créer l'espace"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ════════════════════════════════════════════════════════════════
   DocSpaceForm — ReactiveJSON wrapper (thin shell)
   ════════════════════════════════════════════════════════════════ */

export const DocSpaceForm = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const isOpen = evaluateTemplateValue({
        valueToEvaluate: props.isOpen,
        templateContext,
        globalDataContext,
    });

    const parentId = evaluateTemplateValue({
        valueToEvaluate: props.parentId ?? 0,
        templateContext,
        globalDataContext,
    }) || 0;

    const parentName = evaluateTemplateValue({
        valueToEvaluate: props.parentName ?? '',
        templateContext,
        globalDataContext,
    }) || '';

    const openPath = props.openPath || 'showSpaceForm';

    const handleClose = useCallback(() => {
        globalDataContext.updateData(false, openPath);
    }, [globalDataContext, openPath]);

    return (
        <ActionDependant {...props}>
            <SpaceFormBody
                isOpen={isOpen}
                parentId={parentId}
                parentName={parentName}
                onClose={handleClose}
            />
        </ActionDependant>
    );
};
